/**
 * THE LOTTERY IS RUNNING, said from wherever you are.
 *
 * `design_handoff_order_and_lottery` §3, "Announcement elsewhere": a dark strip above the topnav —
 * green dot, `Lottery live`, which position just came out, and a white `Watch the draw` button.
 * Same idiom as `DraftStrip`; on a phone it goes to the bottom, above the tab bar.
 *
 * IT IS A CLOCK READING, NOT A SUBSCRIPTION. The draw finished the instant the commissioner
 * recorded it; what the league watches is `draftState.lotteryDrawnAt` played back against
 * `revealSchedule`, which is how ten screens land the same name in the same second without
 * anything being broadcast per step. So this module needs no state of its own: give it the stored
 * instant and the current one and it can say what the room is looking at.
 *
 * NO CLOCK ON THIS ONE, unlike the other two strips. The draw is fifty seconds end to end. An
 * elapsed counter reading `0:14` beside it measures nothing anybody is waiting on, and the number
 * that IS worth carrying — how much board is left — is already in the sentence.
 *
 * IT STOPS WHEN THE DRAW STOPS. A strip that stays up after the tenth name is an invitation to
 * watch something that has finished; the board itself is on the Order page for anyone who wants
 * it, and the published order outlives the evening.
 */

import { drawnCount, ordinal, revealDurationMs } from './lotteryReveal.js';

export interface LotteryInfo {
  /** False for all but a minute a year, and the only thing a caller should branch on. */
  live: boolean;
  /** The choosing position that landed most recently — they fill 10 → 1. Null before the first. */
  position: number | null;
  /** Who landed in it. Null before the first name. */
  gm: string | null;
  drawn: number;
  teams: number;
  /** Where the viewer landed, once he has. Null while he is still in. */
  mine: number | null;
}

const OFF: LotteryInfo = {
  live: false, position: null, gm: null, drawn: 0, teams: 0, mine: null,
};

export interface LotteryStripInput {
  /** `draftState.lotteryDrawnAt` — the broadcast. Null until a draw is recorded. */
  drawnAt?: string | null;
  /** The choosing order the draw produced, position 1 first. */
  order: readonly string[];
  teams: number;
  me: string;
  now?: number;
}

export function lotteryStrip(
  { drawnAt, order, teams, me, now = Date.now() }: LotteryStripInput,
): LotteryInfo {
  if (!drawnAt || order.length !== teams || teams <= 0) return OFF;
  const started = Date.parse(drawnAt);
  if (Number.isNaN(started)) return OFF;

  const elapsed = now - started;
  if (elapsed < 0 || elapsed >= revealDurationMs(teams)) return OFF;

  const drawn = drawnCount(elapsed, teams);
  /* Filling from the bottom: after `drawn` names the shallowest one filled is `teams - drawn + 1`. */
  const position = drawn > 0 ? teams - drawn + 1 : null;
  const at = order.findIndex((g) => g.toUpperCase() === me.toUpperCase());
  const minePosition = at + 1;
  const mine = at >= 0 && minePosition > teams - drawn ? minePosition : null;

  return {
    live: true,
    position,
    gm: position === null ? null : (order[position - 1] ?? null),
    drawn,
    teams,
    mine,
  };
}

/**
 * What the strip says, in one clause. The phone gets the short one.
 *
 * The viewer's own result outranks the board's, once it exists — that is the only line on this
 * bar he cannot get by looking at it later.
 */
export function lotterySaid(info: LotteryInfo, narrow = false): string {
  if (!info.live) return '';
  if (info.mine !== null) {
    return narrow
      ? `You choose ${info.mine}${ordinal(info.mine)}`
      : `You choose ${info.mine}${ordinal(info.mine)} of ${info.teams}`;
  }
  if (info.position === null) return narrow ? 'Starting' : 'The draw is starting';
  const where = `${info.position}${ordinal(info.position)} choice is in`;
  return narrow ? where : `${where} — ${info.teams - info.drawn} still to come`;
}
